// @flow

import * as React from 'react';
import IsVisible, { HasBeenVisible } from '@ta/common.is-visible';
import track, { trackClick, type TrackingEventAJAXProps } from './tracking';

/** @Deprecated
 *
 * For clicks, prefer the track/trackClick if duplicates are allowed. Use useGARecordEvent if duplicates are not allowed
 * For mount, prefer to call the track functions on mount (within componentDidMount in a class component, or within a useEffect in a function component)
 * For visible, prefer the useGARecordImpression hook
 * */
type TrackingProps = TrackingEventAJAXProps & { once?: boolean };

type Props = {|
  /** Optional properties to track on click. */
  +click?: TrackingProps,

  /** Optional properties to track on mount */
  +mount?: TrackingProps,

  /** Optional properties to track when this component becomes visible */
  +visible?: TrackingProps,

  /** Optional HTML tag name to insert into the dom. Defaults to div */
  +element: string,

  /** Optional classnames to provide to the injected element */
  +className?: string,

  /** Child nodes to render */
  +children: React.Node,
|};

/**
 * The Track component allows you to track various information about a child while creating only
 * a single dom element. Currently supported are clicks, mount, and visibility tracking.
 */
export default class Track extends React.Component<Props> {
  static defaultProps = {
    element: 'div',
    className: undefined,
  };

  componentDidMount() {
    const { mount } = this.props;
    if (mount && (!mount.once || !this.mountTracked)) {
      track(mount);
      if (mount.once) {
        this.mountTracked = true;
      }
    }
  }

  mountTracked: boolean = false;
  clickTracked: boolean = false;

  render() {
    const options: { [string]: mixed } = { className: this.props.className };
    const { click, visible } = this.props;

    if (click && (!click.once || !this.clickTracked)) {
      options.onClick = e => {
        trackClick(e, click);
        this.clickTracked = true;
      };
    }

    if (visible) {
      if (visible.once) {
        return (
          <HasBeenVisible onVisible={() => track(visible)}>
            {target =>
              React.createElement(
                this.props.element,
                { ...options, ref: target },
                this.props.children,
              )
            }
          </HasBeenVisible>
        );
      }

      return (
        <IsVisible
          onChange={isVisible => {
            if (isVisible) {
              track(visible);
            }
          }}
        >
          {target =>
            React.createElement(
              this.props.element,
              { ...options, ref: target },
              this.props.children,
            )
          }
        </IsVisible>
      );
    }

    return React.createElement(this.props.element, { ...options }, this.props.children);
  }
}
